import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchWorknumberApi } from "../../../../../api/client";
import { mapEligibility } from "./worknumber.mapping";
import type { WorkNumberEligibility } from "./worknumber.types";
import { worknumberKeys } from "./worknumberKeys";

/**
 * Shared queryOptions for an applicant's Work Number eligibility (key + queryFn).
 * Backed by the generated WorkbenchWorknumberApi.getEligibility, whose `object`
 * response is reduced to `{ canPull }` by `mapEligibility`. This is the first
 * call in the flow — its result decides whether the "Pull Purchased Employer
 * Info" button is offered (canPull) or we fall back to reading history.
 *
 * Disabled until both ids are present and the caller opts in, so it never fires
 * a doomed request for an applicant we can't address yet.
 * @param {string} approvalId - The deal / approval identifier (path param).
 * @param {string} applicantId - The applicant identifier (path param).
 * @param {boolean} enabled - Caller opt-in; combined with the presence guards.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function worknumberEligibilityQuery(approvalId: string, applicantId: string, enabled = true) {
  return queryOptions({
    queryKey: worknumberKeys.eligibility({ approvalId, applicantId }),
    queryFn: async ({ signal }): Promise<WorkNumberEligibility> => {
      const response = await workbenchWorknumberApi.getEligibility(approvalId, applicantId, { signal });
      return mapEligibility(response.data);
    },
    staleTime: 30_000,
    // Eligibility is a "does this feature apply?" probe — an error (e.g. the
    // applicant isn't found) is a definitive answer, not a transient failure, so
    // never retry. Retrying would triple the call count and dogpile the BFF.
    retry: false,
    enabled: enabled && !!approvalId && !!applicantId,
  });
}

/**
 * Reads an applicant's Work Number eligibility. Disabled until the approval id
 * and applicant id are present and the caller enables it.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} applicantId - The applicant identifier.
 * @param {{ enabled?: boolean }} [options] - Caller opt-in (defaults to enabled).
 * @param {boolean} [options.enabled] - When false, the query never fires.
 * @returns {ReturnType<typeof useQuery<WorkNumberEligibility>>} The TanStack Query result.
 */
export function useWorknumberEligibility(approvalId: string, applicantId: string, options?: { enabled?: boolean }) {
  return useQuery(worknumberEligibilityQuery(approvalId, applicantId, options?.enabled ?? true));
}
